import { getHeaders, PURCHASER_API_BASE_URL } from "../Constants";

// User info included in responses
export type SourcingRequestUser = {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
};

// RFQ summary for linked RFQs
export type SourcingRequestRfqSummary = {
    id: number;
    uniquePublicId: string;
    title: string | null;
    status: string;
};

// Linked RFQ join table entry
export type SourcingRequestRfq = {
    id: number;
    createdAt: string;
    sourcingRequestId: number;
    rfqId: number;
    rfq: SourcingRequestRfqSummary;
};

// Catalog item summary for items in a sourcing request
export type SourcingRequestCatalogItem = {
    id: number;
    name: string;
    description: string | null;
    labelAssignments?: Array<{
        id: number;
        label: {
            id: number;
            name: string;
            color: string;
        };
    }>;
    preferredVendor?: {
        id: number;
        name: string | null;
    } | null;
    lastVendor?: {
        id: number;
        name: string | null;
    } | null;
    lastRfq?: {
        id: number;
        uniquePublicId: string;
        title: string | null;
    } | null;
    lastQuoted?: {
        uniquePublicId: string;
        mailboxId: number;
        revision: number;
        title: string | null;
    } | null;
};

// Item in a sourcing request (join table entry)
export type SourcingRequestItem = {
    id: number;
    createdAt: string;
    sourcingRequestId: number;
    productCatalogItemId: number;
    productCatalogItem: SourcingRequestCatalogItem;
};

// Sourcing request from list endpoint (summary without full relations)
export type SourcingRequestSummary = {
    id: number;
    name: string;
    description: string | null;
    neededBy: string | null;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    teamId: number;
    createdByUserId: number;
    assignedToUserId: number | null;
    createdByUser: SourcingRequestUser;
    assignedToUser: SourcingRequestUser | null;
    _count: {
        sourcingRequestItems: number;
        sourcingRequestRfqs: number;
    };
};

// Full sourcing request with relations
export type SourcingRequest = SourcingRequestSummary & {
    sourcingRequestItems: SourcingRequestItem[];
    sourcingRequestRfqs: SourcingRequestRfq[];
};

// Filter parameters for list endpoint
export type SourcingRequestFilterParams = {
    search?: string;
    sortBy?: "name" | "updatedAt" | "createdAt" | "neededBy";
    sortOrder?: "asc" | "desc";
    assignedToUserId?: number;
    catalogItemId?: number;
    limit?: number;
    offset?: number;
};

// Create payload
export type CreateSourcingRequestPayload = {
    name: string;
    description?: string;
    neededBy?: string;
    assignedToUserId?: number;
    catalogItemIds?: number[];
};

// Update payload
export type UpdateSourcingRequestPayload = {
    name?: string;
    description?: string;
    neededBy?: string | null;
    assignedToUserId?: number | null;
};

// Add/Remove items payload
export type ModifyItemsPayload = {
    catalogItemIds: number[];
};

// Create RFQ from sourcing request payload
export type CreateRfqFromSourcingRequestPayload = {
    catalogItemIds: number[];
    title?: string;
};

// Link existing RFQ to sourcing request payload
export type LinkRfqPayload = {
    catalogItemIds: number[];
};

// Pagination info from list response
export type SourcingRequestPagination = {
    total: number;
    limit: number;
    offset: number;
};

// List response with pagination
export type SourcingRequestListResponse = {
    data: SourcingRequestSummary[];
    pagination: SourcingRequestPagination;
};

function buildQueryString(filters?: SourcingRequestFilterParams): string {
    if (!filters) return "";

    const params = new URLSearchParams();
    if (filters.search) params.set("search", filters.search);
    if (filters.sortBy) params.set("sortBy", filters.sortBy);
    if (filters.sortOrder) params.set("sortOrder", filters.sortOrder);
    if (filters.assignedToUserId !== undefined) params.set("assignedToUserId", String(filters.assignedToUserId));
    if (filters.catalogItemId !== undefined) params.set("catalogItemId", String(filters.catalogItemId));
    if (filters.limit !== undefined) params.set("limit", String(filters.limit));
    if (filters.offset !== undefined) params.set("offset", String(filters.offset));

    const queryString = params.toString();
    return queryString ? `?${queryString}` : "";
}

export default {
    // List sourcing requests with filtering
    getSourcingRequests: async (filters?: SourcingRequestFilterParams): Promise<SourcingRequestListResponse> => {
        const queryString = buildQueryString(filters);
        const url = PURCHASER_API_BASE_URL + "/v2/sourcing-requests" + queryString;

        const res = await fetch(url, { headers: getHeaders() });
        if (!res.ok) throw new Error("Failed to get sourcing requests");
        return await res.json();
    },

    // Get single sourcing request with full details
    getSourcingRequest: async (id: number): Promise<SourcingRequest> => {
        const url = PURCHASER_API_BASE_URL + `/v2/sourcing-requests/${id}`;
        const res = await fetch(url, { headers: getHeaders() });
        if (!res.ok) throw new Error("Failed to get sourcing request");
        return await res.json();
    },

    // Create new sourcing request
    createSourcingRequest: async (payload: CreateSourcingRequestPayload): Promise<SourcingRequest> => {
        const url = PURCHASER_API_BASE_URL + "/v2/sourcing-requests";
        const res = await fetch(url, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Failed to create sourcing request");
        return await res.json();
    },

    // Update existing sourcing request
    updateSourcingRequest: async (id: number, payload: UpdateSourcingRequestPayload): Promise<SourcingRequest> => {
        const url = PURCHASER_API_BASE_URL + `/v2/sourcing-requests/${id}`;
        const res = await fetch(url, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Failed to update sourcing request");
        return await res.json();
    },

    // Soft delete sourcing request
    deleteSourcingRequest: async (id: number): Promise<void> => {
        const url = PURCHASER_API_BASE_URL + `/v2/sourcing-requests/${id}`;
        const res = await fetch(url, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        });
        if (!res.ok) throw new Error("Failed to delete sourcing request");
    },

    // Add catalog items to sourcing request
    addItemsToSourcingRequest: async (id: number, payload: ModifyItemsPayload): Promise<SourcingRequest> => {
        const url = PURCHASER_API_BASE_URL + `/v2/sourcing-requests/${id}/items`;
        const res = await fetch(url, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Failed to add items to sourcing request");
        return await res.json();
    },

    // Remove catalog items from sourcing request
    removeItemsFromSourcingRequest: async (id: number, payload: ModifyItemsPayload): Promise<SourcingRequest> => {
        const url = PURCHASER_API_BASE_URL + `/v2/sourcing-requests/${id}/items`;
        const res = await fetch(url, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Failed to remove items from sourcing request");
        return await res.json();
    },

    // Create RFQ from selected catalog items in sourcing request
    createRfqFromSourcingRequest: async (id: number, payload: CreateRfqFromSourcingRequestPayload): Promise<{ uniquePublicId: string }> => {
        const url = PURCHASER_API_BASE_URL + `/v2/sourcing-requests/${id}/rfqs`;
        const res = await fetch(url, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Failed to create RFQ from sourcing request");
        return await res.json();
    },

    // Link existing RFQ to sourcing request by adding catalog items to it
    linkRfqToSourcingRequest: async (id: number, rfqUniquePublicId: string, payload: LinkRfqPayload): Promise<SourcingRequestRfq> => {
        const url = PURCHASER_API_BASE_URL + `/v2/sourcing-requests/${id}/rfqs/${rfqUniquePublicId}`;
        const res = await fetch(url, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Failed to link RFQ to sourcing request");
        return await res.json();
    },

    // Unlink RFQ from sourcing request
    unlinkRfqFromSourcingRequest: async (id: number, rfqId: number): Promise<void> => {
        const url = PURCHASER_API_BASE_URL + `/v2/sourcing-requests/${id}/rfqs/${rfqId}`;
        const res = await fetch(url, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        });
        if (!res.ok) throw new Error("Failed to unlink RFQ from sourcing request");
    },
};
